import React from "react";
import { useGetClientQuery } from "actions/clients/queries";
import { useCreateClientMutation } from "actions/clients/mutations";
import { useAddProduitMutation } from "actions/produits/mutations";
import { useGetProduitsQuery } from "actions/produits/queries";
import { useCreateVenteMutation } from "actions/ventes/mutations";
import FileUpload from "components/AddDepenses/FileUpload";
import ProductModal from "components/AddProducts/ProductModal";
import ProductForm from "components/ProductForm/ProductForm";
import AutocompleteSelect from "components/AutocompleteSelect/AutocompleteSelect";
import { useEffect, useState, useRef } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import HeaderFirst from "utils/HeaderFirst";
import { useGetComptePaiementQuery } from "actions/compte_paiement/queries";
import { useGetCodesQuery } from "actions/comptabilite/queries";
import { toastApiErrors } from "utils/toastApiError";
import "../client.css";

export default function AddVentesForm() {
  const { clientID: dossier_id } = useParams();
  const navigate = useNavigate();
  const [addVente, { isLoading: isSubmittingVente }] = useCreateVenteMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: produitsData, refetch: refetchProduits } = useGetProduitsQuery({ dossier_id });
  const { data: clientsData, refetch: refetchClients } = useGetClientQuery({ dossier_id });


  const [createClient] = useCreateClientMutation();
  const { data: compte_paiement } = useGetComptePaiementQuery({ dossier_id });
  const { data: codesData } = useGetCodesQuery({ dossier_client: dossier_id });

  // Comptes proposables pour une vente : classe 7 (produits) + 8 (HAO/cessions)
  const rawCodes = Array.isArray(codesData)
    ? codesData
    : codesData?.data || codesData?.results || [];
  const accountOptions = rawCodes
    .filter((c) => {
      const s = String(c.code ?? "");
      return s.startsWith("7") || s.startsWith("8");
    })
    .map((c) => ({ id: Number(c.code), nom: c.combine || `${c.code} - ${c.libelle}` }));

  const [showProductModal, setShowProductModal] = useState(false);
  const productFormMethods = useForm();
  const [createProduit, { isSuccess: isProductSuccess }] = useAddProduitMutation();
  const [modeReglement, setModeReglement] = useState("comptant");

  const handleAddProduct = () => setShowProductModal(true);
  const handleCloseModal = () => setShowProductModal(false);

  const handleCreateProduct = async (nom) => {
    try {
      const produitData = {
        nom: nom,
        dossier_id: dossier_id,
        prix: 0,
        quantite: 1,
        genre_transaction: "entree",
      };
      const result = await createProduit(produitData).unwrap();
      toast.success(`Produit "${nom}" créé avec succès`);
      await refetchProduits();
      return result;
    } catch (error) {
      console.error('Erreur création produit:', error);
      toast.error('Erreur lors de la création du produit');
      throw error;
    }
  };

  const handleCreateClient = async (nom) => {
    try {
      const clientData = {
        nom: nom,
        dossier_id: dossier_id,
        telephone: '',
        email: '',
        adresse: '',
        active: true,
        archived: false,
        compte_comptable: '41',  // Génère automatiquement le compte client
        lettrage: false
      };
      
      const result = await createClient(clientData).unwrap();
      toast.success(`Client "${nom}" créé avec succès`);
      await refetchClients();
      return result;
    } catch (error) {
      console.error('Erreur création client:', error);
      toast.error('Erreur lors de la création du client');
      throw error;
    }
  };

  const lastAutoFilledIntitule = useRef("");

  const handleProductCreated = async (createdProduct) => {
    await refetchProduits();
    if (!createdProduct?.id) return;

    if (createdProduct?.nom && (!vente.intitule || vente.intitule === lastAutoFilledIntitule.current)) {
      setVente((prev) => ({ ...prev, intitule: createdProduct.nom }));
      lastAutoFilledIntitule.current = createdProduct.nom;
    }

    setVente((prev) => {
      const produits_associes = [...prev.produits_associes];
      const pendingIndex = produits_associes.findIndex(
        (p) => !p.produit || Number.isNaN(Number(p.produit))
      );
      const index =
        pendingIndex >= 0 ? pendingIndex : produits_associes.length - 1;

      produits_associes[index] = {
        ...produits_associes[index],
        produit: createdProduct.id,
        code_comptable: createdProduct.code_comptable || produits_associes[index]?.code_comptable,
      };

      return { ...prev, produits_associes };
    });
  };

  const [vente, setVente] = useState({
    client: "",
    intitule: "",
    dossier_id: dossier_id,
    facturable_au_client: false,
    source_paiement: "",
    crm: "",
    taxes: 0,
    prix: 0,
    somme_payee: 0,
    remise: 0,
    quantite: 1,
    description: "",
    date_transaction: "",
    produits_associes: [{ produit: null, prix: null, quantite: 1 }],
  });

  const taxesOptions = { 0: "0%", 10: "10%", 18: "18%" };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    if (name === "taxes" && parseFloat(value) > 100) {
      setVente((prev) => ({ ...prev, [name]: "100" }));
    } else {
      setVente((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const addProduct = () => {
    setVente((prev) => ({
      ...prev,
      produits_associes: [...prev.produits_associes, { produit: "", prix: "", quantite: 1 }],
    }));
  };

  useEffect(() => {
    if (isProductSuccess) setShowProductModal(false);
  }, [isProductSuccess]);

  const removeProduct = (index) => {
    setVente((prev) => ({
      ...prev,
      produits_associes: prev.produits_associes.filter((_, i) => i !== index),
    }));
  };

  const [selectedFile, setSelectedFile] = useState(null);
  const handleFileSelect = (file) => setSelectedFile(file);

  const handleProductChange = (index, event) => {
    const { name, value } = event.target;
    let updatedProduitsAssocies = [...vente.produits_associes];
    if (!updatedProduitsAssocies[index]) {
      updatedProduitsAssocies[index] = { produit: "", prix: "", quantite: 1 };
    }
    updatedProduitsAssocies[index][name] = name === "produit" ? Number(value) : value;

    // À la sélection d'un produit, pré-remplir le compte avec son code par défaut et l'intitulé
    if (name === "produit" && value && !Number.isNaN(Number(value))) {
      const prod = (produitsData?.data || []).find((p) => p.id === Number(value));
      if (prod?.code_comptable) {
        updatedProduitsAssocies[index].code_comptable = prod.code_comptable;
      }
      if (prod?.nom && (!vente.intitule || vente.intitule === lastAutoFilledIntitule.current)) {
        setVente((prev) => ({ ...prev, intitule: prod.nom }));
        lastAutoFilledIntitule.current = prod.nom;
      }
    }

    // À la sélection d'un compte comptable sur la ligne, mettre à jour l'intitulé si vide ou précédent
    if (name === "code_comptable" && value) {
      const digits = String(value).replace(/\D/g, "");
      const found = accountOptions.find((a) => String(a.id) === digits);
      const rawNom = found?.nom || "";
      const accountLabel = rawNom.includes(" - ") ? rawNom.split(" - ").slice(1).join(" - ").trim() : rawNom;
      if (accountLabel && (!vente.intitule || vente.intitule === lastAutoFilledIntitule.current)) {
        setVente((prev) => ({ ...prev, intitule: accountLabel }));
        lastAutoFilledIntitule.current = accountLabel;
      }
    }

    if (value === "ajouterProduit") {
      handleAddProduct();
      return;
    }
    setVente((prev) => ({ ...prev, produits_associes: updatedProduitsAssocies }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting || isSubmittingVente) {
      return;
    }

    const requiredFields = ["client", "intitule", "description"];
    const areFieldsFilled = requiredFields.every(
      (field) => vente[field] && vente[field].toString().trim() !== ""
    );

    if (!areFieldsFilled || !vente.produits_associes.length) {
      toast.error("Tous les champs sont obligatoires.");
      return;
    }

    // Nettoyer code_comptable : garder uniquement les codes numériques
    const produitsAssociesClean = vente.produits_associes.map((p) => {
      const code = String(p.code_comptable ?? "").replace(/\D/g, "");
      return { ...p, code_comptable: code ? Number(code) : null };
    });

    let dataPayload = {
      client: vente.client,
      intitule: vente.intitule,
      dossier_id: vente.dossier_id,
      facturable_au_client: vente.facturable_au_client,
      taxes: vente.taxes,
      description: vente.description,
      crm: vente.crm,
      source_paiement: vente.source_paiement,
      mode_reglement: modeReglement,
      produits_associes: produitsAssociesClean,
      prix: vente.prix,
      somme_payee: modeReglement === "credit" ? 0 : vente.somme_payee,
      remise: vente.remise,
      date_transaction: vente.date_transaction || null,
    };

    setIsSubmitting(true);
    try {
      if (selectedFile) {
        const formData = new FormData();
        formData.append("dossier_id", vente.dossier_id);
        formData.append("client", vente.client);
        formData.append("intitule", vente.intitule);
        formData.append("facturable_au_client", vente.facturable_au_client);
        formData.append("taxes", vente.taxes);
        formData.append("description", vente.description);
        formData.append("crm", vente.crm);
        formData.append("source_paiement", vente.source_paiement);
        formData.append("mode_reglement", modeReglement);
        formData.append("prix", vente.prix);
        formData.append("somme_payee", modeReglement === "credit" ? 0 : vente.somme_payee);
        formData.append("date_transaction", vente.date_transaction || "");
        formData.append("remise", vente.remise);
        vente.produits_associes.forEach((produit, index) => {
          if (produit.produit && produit.prix !== undefined) {
            formData.append(`produits_associes[${index}]produit`, produit.produit);
            formData.append(`produits_associes[${index}]prix`, produit.prix.toString());
            if (produit.quantite) {
              formData.append(`produits_associes[${index}]quantite`, produit.quantite.toString());
            }
            const code = String(produit.code_comptable ?? "").replace(/\D/g, "");
            if (code) {
              formData.append(`produits_associes[${index}]code_comptable`, code);
            }
          } else {
            console.error(`Missing required product details at index ${index}`, produit);
          }
        });
        formData.append("piece_joint", selectedFile);
        await addVente(formData).unwrap();
        toast.success("Vente ajoutée avec succès");
        navigate(-1);
      } else {
        await addVente(dataPayload, { headers: { "Content-Type": "application/json" } }).unwrap();
        toast.success("Vente ajoutée avec succès");
        navigate(-1);
      }
    } catch (error) {
      console.error("Requête échouée:", error);
      toastApiErrors(error);
    } finally {
      setIsSubmitting(false);
    }
  };


  // Calcul automatique du prix total + répercussion sur somme_payee si comptant
  useEffect(() => {
    let somme = 0;
    const total = vente.produits_associes.map((produit) => {
      const prix = parseInt(produit.prix) || 0;
      const quantite = parseInt(produit.quantite) || 0;
      somme += prix * quantite;
      return somme;
    });

    const montantHT = total[total.length - 1] || 0;
    const taxes = parseFloat(vente.taxes) || 0;
    const remise = parseFloat(vente.remise) || 0;
    const montant_TTC = montantHT * (1 + (taxes - remise) / 100);

    setVente((prev) => ({
      ...prev,
      prix: montant_TTC || 0,
      ...(modeReglement === "comptant" && { somme_payee: montant_TTC || 0 }),
    }));
  }, [vente.produits_associes, vente.taxes, vente.remise, modeReglement]);

  return (
    <div style={{ backgroundColor: "white", minHeight: "500px", borderRadius: "20px", padding: "15px" }}>
      <HeaderFirst
        nom={<h4 className="titre-dossier mb-0">Ajouter une vente</h4>}
      />

      <div className="w-100 w-md-75 px-0 px-md-5 container-background">
        <Container fluid className="container-style p-4 mb-5">
          <Form onSubmit={handleSubmit}>
            <div className="d-flex flex-column align-items-center gap-2">

              {/* Client + Intitulé */}
              <div className="w-100">
                <Row>
                  <Col sm={6}>
                    <Form.Group controlId="formClient">
                      <AutocompleteSelect
                        label="Client"
                        options={clientsData?.data || []}
                        value={vente.client}
                        onChange={(value) => {
                          setVente(prev => ({ ...prev, client: value }));
                        }}
                        onCreateNew={handleCreateClient}
                        placeholder="Saisissez ou sélectionnez un client"
                        createText="Créer le client"
                        type="client"
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group controlId="formIntitule">
                      <Form.Label>Intitulé</Form.Label>
                      <Form.Control
                        type="text"
                        name="intitule"
                        value={vente.intitule}
                        onChange={handleInputChange}
                        className="rounded rounded-pill"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>
              <div className="w-100">
                <Row className="row-gap-2">
                  <Col sm={6}>
                    <Form.Group controlId="formDateTransaction">
                      <Form.Label className="mb-2">Date de la transaction</Form.Label>
                      <Form.Control
                        type="date"
                        name="date_transaction"
                        value={vente.date_transaction}
                        onChange={handleInputChange}
                        className="rounded rounded-pill"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>            
              {/* Source paiement + Description */}
              <div className="w-100">
                <Row>
                  <Col sm={6}>
                    <Form.Group controlId="formSourcePaiement">
                      <Form.Label>Source de Paiement</Form.Label>
                      <Form.Control
                        as="select"
                        name="source_paiement"
                        value={vente.source_paiement}
                        onChange={handleInputChange}
                        className="rounded rounded-pill"
                      >
                        <option value="">- Sélectionnez -</option>
                        {compte_paiement?.data?.map((compte) => (
                          <option key={compte.id} value={compte.id}>{compte.compte}</option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group controlId="formDescription">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        type="text"
                        name="description"
                        value={vente.description}
                        onChange={handleInputChange}
                        className="rounded rounded-pill"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Taxes + Remise */}
              <div className="w-100">
                <Row>
                  <Col sm={6}>
                    <Form.Group controlId="formTaxes">
                      <Form.Label>Taxes (%)</Form.Label>
                      <Form.Control
                        as="select"
                        name="taxes"
                        value={vente.taxes}
                        onChange={handleInputChange}
                        className="rounded rounded-pill"
                      >
                        <option value="">- Sélectionnez -</option>
                        {Object.entries(taxesOptions).map(([value, label]) => (
                          <option key={value} value={parseInt(value)}>{label}</option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                  <Col sm={6}>
                    <Form.Group controlId="formRemise">
                      <Form.Label>Remise (%)</Form.Label>
                      <Form.Control
                        type="number"
                        name="remise"
                        value={vente.remise}
                        onChange={handleInputChange}
                        className="rounded rounded-pill"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Mode de règlement */}
              <div className="w-100">
                <Row>
                  <Col sm={12}>
                    <Form.Group>
                      <Form.Label style={{ fontWeight: 600 }}>Mode de règlement</Form.Label>
                      <div style={{ display: "flex", gap: "24px", marginTop: "6px" }}>
                        {[
                          { value: "comptant", label: "Au comptant" },
                          { value: "credit", label: "À crédit" },
                        ].map((mode) => (
                          <label
                            key={mode.value}
                            style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px" }}
                          >
                            <input
                              type="radio"
                              name="modeReglement"
                              value={mode.value}
                              checked={modeReglement === mode.value}
                              onChange={() => {
                                setModeReglement(mode.value);
                                if (mode.value === "comptant") {
                                  setVente((prev) => ({ ...prev, somme_payee: prev.prix }));
                                }
                              }}
                              style={{ accentColor: "#009688" }}
                            />
                            {mode.label}
                          </label>
                        ))}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Montant payé (crédit uniquement) + Total dû */}
              <div className="w-100">
                <Row>
                  {modeReglement === "credit" && (
                    <Col sm={6}>
                      <Form.Group controlId="formSommePayee">
                        <Form.Label>Montant payé</Form.Label>
                        <Form.Control
                          type="number"
                          name="somme_payee"
                          value={vente.somme_payee}
                          onChange={handleInputChange}
                          className="rounded rounded-pill"
                        />
                      </Form.Group>
                    </Col>
                  )}
                  <Col sm={6}>
                    <Form.Group controlId="formPrix">
                      <Form.Label>Total dû</Form.Label>
                      <Form.Control
                        type="number"
                        name="prix"
                        value={vente.prix}
                        disabled={true}
                        className="rounded rounded-pill"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Produits */}
              <div className="w-100">
                <Row>
                  <Col>
                    <div className="vente-product-list">
                      {vente.produits_associes.map((product, index) => (
                        <ProductForm
                          key={index}
                          product={product}
                          index={index}
                          handleProductChange={handleProductChange}
                          removeProduct={removeProduct}
                          productOptions={produitsData?.data}
                          totalProducts={vente.produits_associes?.length}
                          mobileTwoLines
                          onCreateNewProduct={handleCreateProduct}
                          accountOptions={accountOptions}
                          showAccount
                        />
                      ))}
                    </div>
                    <Button
                      onClick={addProduct}
                      className="mt-3 rounded rounded-pill"
                      style={{ backgroundColor: "#068f7a", borderColor: "#068f7a" }}
                    >
                      Ajouter un produit
                    </Button>
                  </Col>
                </Row>
              </div>

              {/* Pièce jointe */}
              <div className="w-100">
                <Row>
                  <Form.Group controlId="fileUploadControl" className="w-100">
                    <FileUpload onFileSelect={handleFileSelect} />
                  </Form.Group>
                </Row>
              </div>

              {/* Facturable au client */}
              <div className="w-100">
                <Row>
                  <Col sm={12}>
                    <Form.Group controlId="formFacturableAuClient">
                      <Form.Check
                        type="checkbox"
                        label="Facturable au client"
                        name="facturable_au_client"
                        checked={vente.facturable_au_client}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Boutons */}
              <Row className="mt-3">
                <Col sm={12} style={{ textAlign: "right" }}>
                  <div className="d-flex gap-3">
                    <button onClick={() => navigate(-1)} className="btn btn-danger rounded rounded-pill px-4">
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary rounded rounded-pill px-4 d-inline-flex align-items-center justify-content-center"
                      style={{ backgroundColor: "#068f7a", borderColor: "#068f7a" }}
                      disabled={isSubmitting || isSubmittingVente || !vente.client || !vente.intitule || !vente.prix}
                    >
                      {(isSubmitting || isSubmittingVente) && (
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                      )}
                      {isSubmitting || isSubmittingVente ? "Enregistrement..." : "Enregistrer"}
                    </button>

                  </div>
                </Col>
              </Row>

            </div>
          </Form>
        </Container>

        <ProductModal
          showModal={showProductModal}
          handleClose={handleCloseModal}
          formMethods={productFormMethods}
          creationProduit={createProduit}
          dossier_id={dossier_id}
          produits={produitsData?.data}
          type="vente"
          onProductCreated={handleProductCreated}
        />
      </div>
    </div>
  );
}